import React, {useRef} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import PageTitle from '../../../../uiToolkit/PageTitle';
import SearchInput from '../../../../uiToolkit/Inputs/SearchInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import PostsEmptyState from '../../../../uiToolkit/EmptyStates/PostsEmptyState';
import {PostListItem} from '../../../../data/models/Posts/Post';
import {postsText} from '../../postsText';
import PostCard from '../components/PostCard';
import styles from './PostsListUI.module.css';

interface Props {
  posts: PostListItem[];
  isLoading: boolean;
  search: string;
  setSearch: (search: string) => void;
  onCreate: () => void;
  onOpen: (post: PostListItem) => void;
  onEditInfo: (post: PostListItem) => void;
  onLearners: (post: PostListItem) => void;
  onLearnerProgress: (post: PostListItem) => void;
  onDelete: (post: PostListItem) => void;
  onMoveToProject: (post: PostListItem) => void;
  loadNextPage: () => Promise<any>;
}

const PostsListUI = React.memo(
  ({
    posts,
    isLoading,
    search,
    setSearch,
    onCreate,
    onOpen,
    onEditInfo,
    onLearners,
    onLearnerProgress,
    onDelete,
    onMoveToProject,
    loadNextPage,
  }: Props) => {
    const observer = useRef<any>();
    const overscrollLock = useRef(false);

    const lastPostElementRef = (node: any) => {
      if (isLoading || overscrollLock.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          overscrollLock.current = true;
          loadNextPage().then(() => {
            overscrollLock.current = false;
          });
        }
      });

      if (node) observer.current.observe(node);
    };

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <PageTitle title={postsText.title} />
          <div className={styles.filters}>
            <SearchInput value={search} onChange={setSearch} />
            <Button
              title={postsText.newPost}
              onClick={onCreate}
              uiType="action"
              icon="icon-plus"
            />
          </div>
        </div>
        <div>
          <Container fluid className={styles.list}>
            <Row>
              {posts.length ? (
                posts.map((post, index) => (
                  <Col
                    xl={3}
                    lg={4}
                    key={post.id}
                    className={styles.col}
                    ref={
                      posts.length === index + 1 ? lastPostElementRef : null
                    }>
                    <PostCard
                      post={post}
                      onOpen={onOpen}
                      onEditInfo={onEditInfo}
                      onLearners={onLearners}
                      onLearnerProgress={onLearnerProgress}
                      onDelete={onDelete}
                      onMoveToProject={onMoveToProject}
                    />
                  </Col>
                ))
              ) : isLoading ? (
                <Col xl={12} lg={12}>
                  <div className={styles.loading}>
                    <LoadingSpinner size={48} />
                  </div>
                </Col>
              ) : (
                <Col xl={12} lg={12}>
                  <PostsEmptyState
                    title={
                      search ? postsText.searchEmptyTitle : postsText.emptyTitle
                    }
                    subtitle={
                      search
                        ? postsText.searchEmptySubtitle
                        : postsText.emptySubtitle
                    }
                  />
                </Col>
              )}
            </Row>
          </Container>
        </div>
      </div>
    );
  },
);

export default PostsListUI;
